import type { Session } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useDb, type Db } from './supabase';

interface AuthState {
  /** `undefined` while the stored session is being read. */
  session: Session | null | undefined;
}

const AuthContext = createContext<AuthState>({ session: undefined });

/** Keeps the signed-in session (stored by supabase-js in the browser) in React state. */
export function AuthProvider({ children }: { children: ReactNode }) {
  const db = useDb();
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  useEffect(() => {
    let active = true;
    void db.auth.getSession().then(({ data }) => {
      if (active) {
        setSession(data.session);
      }
    });
    const { data } = db.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [db]);
  return <AuthContext.Provider value={{ session }}>{children}</AuthContext.Provider>;
}

export function useSession(): Session | null | undefined {
  return useContext(AuthContext).session;
}

/**
 * Sign out of this browser only. Without `scope: 'local'` supabase-js ends every session of the
 * person, also on their phone (one account can be Admin in the office and Master on board, D124).
 */
export function signOutHere(db: Pick<Db, 'auth'>): Promise<unknown> {
  return db.auth.signOut({ scope: 'local' });
}
